// FLAGS: none
// Placeholder for the scheduler's own event list; fetches nothing, links to the create-event route.
import { useState } from "react";
import { CalendarPlus, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { SpaceEventSummary } from "@src/models/spaces";
import { formatSessionDateTime } from "@src/utils/format";
import { useEvents } from "@src/queries/events";

interface EventGroupProps {
  title: string;
  events: SpaceEventSummary[];
  /** Past rows are visually muted. */
  muted?: boolean;
  /** Archival groups (Past, Cancelled) start folded. */
  defaultCollapsed?: boolean;
}

const EventGroup = ({
  title,
  events,
  muted = false,
  defaultCollapsed = false,
}: EventGroupProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 first:mt-0">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((current) => !current)}
        className="mb-2 flex w-full items-center gap-1 text-sm font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${collapsed ? "-rotate-90" : ""}`}
          aria-hidden
        />
        {title} ({events.length})
      </button>
      {!collapsed && (
        // Cap the group at roughly four and a half rows so long histories
        // scroll inside their section instead of stretching the page.
        <ul className="flex max-h-[34rem] flex-col gap-3 overflow-y-auto pr-1">
          {events.map((event) => (
            <li key={event.id}>
              <Link
                to={`/events/${event.id}`}
                className={`flex items-center justify-between rounded-xl border border-gray-200 p-4 transition-colors hover:border-green-500 ${
                  muted ? "bg-gray-50" : "bg-white"
                }`}
              >
                <div>
                  <p
                    className={`font-medium ${muted ? "text-gray-600" : "text-indigo-800"}`}
                  >
                    {event.title}
                  </p>
                  <p className="text-sm text-gray-600">
                    {event.employerName} · {event.sessionCount}{" "}
                    {event.sessionCount === 1 ? "session" : "sessions"}
                    {event.nextSessionAt &&
                      ` · ${formatSessionDateTime(event.nextSessionAt)}`}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Scheduled by {event.scheduledBy}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EventsListPage = () => {
  const { data: overview, isLoading, isError } = useEvents();
  const isEmpty =
    overview !== undefined &&
    overview.upcoming.length === 0 &&
    overview.past.length === 0 &&
    overview.cancelled.length === 0;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-indigo-800">Events</h1>
        <Button asChild variant="default" size="default">
          <Link to="/events/new">
            <CalendarPlus className="mr-1 h-5 w-5" aria-hidden />
            Create event
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {isError && (
        <p className="text-red-600">Couldn&apos;t load your events.</p>
      )}

      {isEmpty && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="mb-4 text-gray-600">
            No events yet. Create your first Space.
          </p>
          <Link
            to="/events/new"
            className="inline-flex items-center gap-2 font-medium text-green-700 hover:text-green-900"
          >
            <CalendarPlus className="h-5 w-5" aria-hidden />
            Create an event
          </Link>
        </div>
      )}

      {overview && !isEmpty && (
        <>
          <EventGroup title="Upcoming" events={overview.upcoming} />
          <EventGroup title="Past" events={overview.past} muted />
          <EventGroup
            title="Cancelled"
            events={overview.cancelled}
            muted
            defaultCollapsed
          />
        </>
      )}
    </div>
  );
};

export default EventsListPage;
